import { LoaderIcon, RefreshCwIcon, Trash2Icon } from "lucide-react";

import { cn } from "../../lib/utils";
import { RowActionIconButton } from "../ui/row-action-icon-button";

export interface ProviderAccountListItem {
  readonly id: string;
  readonly credentialTarget: "primary" | "linked";
  readonly accountDisplayName?: string | null;
  readonly accountEmail: string | null;
  readonly planLabel: string | null;
  readonly isDefaultForNewChats: boolean;
  readonly needsReconnect: boolean;
  readonly canDisconnect?: boolean;
}

interface ProviderAccountManageRowProps {
  readonly account: ProviderAccountListItem;
  readonly defaultScopeLabel: string;
  readonly isBusy: boolean;
  /**
   * Whether a LINKED row may offer reconnect. It needs a server that replaces
   * that row's credential in place; without one, signing in as the same account
   * is refused as a duplicate and the button is a dead end. CMMD supports this
   * for Claude and not yet for Codex.
   */
  readonly canReconnectLinkedAccount: boolean;
  readonly onReconnect: (account: ProviderAccountListItem) => void;
  readonly onDisconnect: () => void;
  readonly onRemoveAccount?: ((accountId: string) => void) | undefined;
  readonly isRemoving: boolean;
}

export function ProviderAccountManageRow({
  account,
  defaultScopeLabel,
  isBusy,
  canReconnectLinkedAccount,
  onReconnect,
  onDisconnect,
  onRemoveAccount,
  isRemoving,
}: ProviderAccountManageRowProps) {
  const primaryLabel = account.accountDisplayName ?? account.accountEmail;
  const isPrimaryLabelKnown = primaryLabel !== null;

  return (
    <div
      data-slot="provider-account-row"
      className="group flex min-h-18 items-center gap-3 px-3 py-3 transition-colors hover:bg-muted/20 sm:px-4"
    >
      <span
        aria-hidden="true"
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-full border text-xs font-medium uppercase",
          isPrimaryLabelKnown
            ? "border-border/70 bg-muted/50 text-foreground"
            : "border-dashed border-border bg-muted/30 text-muted-foreground",
        )}
      >
        {primaryLabel?.charAt(0) ?? "?"}
      </span>
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            "truncate text-[13px] font-medium leading-5",
            isPrimaryLabelKnown ? "text-foreground" : "text-muted-foreground",
          )}
        >
          {primaryLabel ?? "Identity unavailable"}
        </div>
        {account.accountDisplayName !== null && account.accountEmail !== null ? (
          <div className="truncate text-xs leading-5 text-muted-foreground">
            {account.accountEmail}
          </div>
        ) : null}
        <ProviderAccountStatus account={account} defaultScopeLabel={defaultScopeLabel} />
      </div>
      <ProviderAccountAction
        account={account}
        isBusy={isBusy}
        canReconnectLinkedAccount={canReconnectLinkedAccount}
        onReconnect={onReconnect}
        onDisconnect={onDisconnect}
        onRemoveAccount={onRemoveAccount}
        isRemoving={isRemoving}
      />
    </div>
  );
}

function ProviderAccountStatus({
  account,
  defaultScopeLabel,
}: {
  readonly account: ProviderAccountListItem;
  readonly defaultScopeLabel: string;
}) {
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-[11px] leading-5 text-muted-foreground">
      {account.planLabel === null ? null : <span>{account.planLabel}</span>}
      {account.planLabel === null ? null : <span aria-hidden="true">·</span>}
      <span
        className={cn(
          account.isDefaultForNewChats ? "text-success-foreground" : null,
          account.needsReconnect ? "text-warning-foreground" : null,
        )}
      >
        {account.needsReconnect
          ? "Reconnect required"
          : account.isDefaultForNewChats
            ? defaultScopeLabel
            : "Connected, not in use"}
      </span>
    </span>
  );
}

interface RowActionAvailability {
  readonly reconnect: boolean;
  readonly remove: boolean;
  readonly disconnect: boolean;
  readonly any: boolean;
}

/**
 * Which controls a row is entitled to.
 *
 * A row that says "Reconnect required" and offers only a bin was the whole of
 * FOR-36204: the linked check used to run first and return, so the reconnect
 * branch beneath it was unreachable for exactly the accounts that needed it.
 * Reconnect and Remove are independent here, and a linked row gets both.
 */
function rowActionAvailability(
  account: ProviderAccountListItem,
  canReconnectLinkedAccount: boolean,
  canRemove: boolean,
): RowActionAvailability {
  const isLinked = account.credentialTarget === "linked";
  const reconnect = account.needsReconnect && (!isLinked || canReconnectLinkedAccount);
  const remove = isLinked && canRemove;
  const disconnect = account.canDisconnect === true;
  return { reconnect, remove, disconnect, any: reconnect || remove || disconnect };
}

function ProviderAccountAction({
  account,
  isBusy,
  canReconnectLinkedAccount,
  onReconnect,
  onDisconnect,
  onRemoveAccount,
  isRemoving,
}: Omit<ProviderAccountManageRowProps, "defaultScopeLabel" | "accountName">) {
  const available = rowActionAvailability(
    account,
    canReconnectLinkedAccount,
    onRemoveAccount !== undefined,
  );
  if (!available.any) return null;
  const identity = accountActionIdentity(account);

  return (
    <div className="flex shrink-0 flex-wrap justify-end gap-2">
      {available.reconnect ? (
        <RowActionIconButton
          label={`Reconnect ${identity}`}
          icon={<RefreshCwIcon />}
          disabled={isBusy}
          onClick={() => onReconnect(account)}
        />
      ) : null}
      {available.remove ? (
        <RemoveAccountAction
          accountId={account.id}
          identity={identity}
          disabled={isBusy}
          isRemoving={isRemoving}
          onRemoveAccount={onRemoveAccount}
        />
      ) : null}
      {available.disconnect ? (
        <RowActionIconButton
          label={`Disconnect ${identity}`}
          icon={<Trash2Icon />}
          variant="destructive-outline"
          disabled={isBusy}
          onClick={onDisconnect}
        />
      ) : null}
    </div>
  );
}

function RemoveAccountAction({
  accountId,
  identity,
  disabled,
  isRemoving,
  onRemoveAccount,
}: {
  readonly accountId: string;
  readonly identity: string;
  readonly disabled: boolean;
  readonly isRemoving: boolean;
  readonly onRemoveAccount: ((accountId: string) => void) | undefined;
}) {
  return (
    <RowActionIconButton
      label={`Remove ${identity}`}
      icon={isRemoving ? <LoaderIcon className="animate-spin" /> : <Trash2Icon />}
      variant="destructive-outline"
      disabled={disabled || isRemoving}
      onClick={() => onRemoveAccount?.(accountId)}
    />
  );
}

function accountActionIdentity(account: ProviderAccountListItem): string {
  return account.accountDisplayName ?? account.accountEmail ?? "unknown account";
}
